import { Action, Module, Mutation } from "vuex-class-modules"

import {
  CalendarEventDto,
  CalendarEventQueryDto,
  CreateCalendarEventRequestDto,
  UpdateCalendarEventRequestDto
} from "@/models/Calendar"
import { BaseModule } from "@/models/BaseModule"
import { apiService } from "@/services"
import { Endpoint } from "@/services/endpoints"

import { accountModule } from "./account.module"

import { store } from "../store"

export enum CalendarActions {
  GET_CALENDAR_EVENTS = "GET_CALENDAR_EVENTS",
  GET_CALENDAR_EVENTS_FOR_WINDOW = "GET_CALENDAR_EVENTS_FOR_WINDOW",
  SET_CALENDAR_EVENTS = "SET_CALENDAR_EVENTS",
  CREATE_CALENDAR_EVENT = "CREATE_CALENDAR_EVENT",
  UPDATE_CALENDAR_EVENT = "UPDATE_CALENDAR_EVENT",
  DELETE_CALENDAR_EVENT = "DELETE_CALENDAR_EVENT",
  RESET_CALENDAR_EVENTS = "RESET_CALENDAR_EVENTS"
}

const sortCalendarEvents = (events: CalendarEventDto[]) =>
  [...events].sort(
    (left, right) => new Date(left.startDate).getTime() - new Date(right.startDate).getTime()
  )

@Module
class CalendarModule extends BaseModule {
  events: CalendarEventDto[] = []

  get accountId() {
    return accountModule.accountId
  }

  get [`get/${CalendarActions.GET_CALENDAR_EVENTS}`]() {
    return this.events
  }

  @Mutation
  setCalendarEvents(events: CalendarEventDto[]) {
    this.events = sortCalendarEvents(events.map(event => new CalendarEventDto(event)))
  }

  @Mutation
  upsertCalendarEvent(event: CalendarEventDto) {
    const normalizedEvent = new CalendarEventDto(event)
    const index = this.events.findIndex(currentEvent => currentEvent.id === event.id)
    if (index === -1) {
      this.events = sortCalendarEvents([...this.events, normalizedEvent])
      return
    }

    const nextEvents = [...this.events]
    nextEvents.splice(index, 1, normalizedEvent)
    this.events = sortCalendarEvents(nextEvents)
  }

  @Mutation
  removeCalendarEvent(eventId: string) {
    this.events = this.events.filter(event => event.id !== eventId)
  }

  @Mutation
  resetCalendarEvents() {
    this.events = []
  }

  /**
   * Loads account calendar events for the selected window and stores them for reuse across calendar interactions.
   */
  @Action
  async [CalendarActions.GET_CALENDAR_EVENTS](query: CalendarEventQueryDto) {
    const data = await apiService.get<CalendarEventDto[]>(
      Endpoint.CALENDAR_EVENTS(this.accountId),
      query.getJsonObj()
    )

    return this.handleResponse<CalendarEventDto[]>(data, response => {
      this.setCalendarEvents(response)
      return response
    })
  }

  /** Fetches a second date window without replacing the events used by the calendar grid. */
  @Action
  async [CalendarActions.GET_CALENDAR_EVENTS_FOR_WINDOW](query: CalendarEventQueryDto) {
    const data = await apiService.get<CalendarEventDto[]>(
      Endpoint.CALENDAR_EVENTS(this.accountId),
      query.getJsonObj()
    )

    return this.handleResponse<CalendarEventDto[]>(
      data,
      response => response,
      undefined,
      undefined,
      true
    ) as CalendarEventDto[] | false
  }

  @Action
  async [CalendarActions.SET_CALENDAR_EVENTS](events: CalendarEventDto[]) {
    this.setCalendarEvents(events)
  }

  @Action
  async [CalendarActions.CREATE_CALENDAR_EVENT](request: CreateCalendarEventRequestDto) {
    const data = await apiService.post<CalendarEventDto>(
      Endpoint.CALENDAR_EVENTS(this.accountId),
      request.getJsonObj()
    )

    return this.handleResponse<CalendarEventDto>(data, response => {
      this.upsertCalendarEvent(response)
      return response
    })
  }

  @Action
  async [CalendarActions.UPDATE_CALENDAR_EVENT]({
    eventId,
    request
  }: {
    eventId: string
    request: UpdateCalendarEventRequestDto
  }) {
    const data = await apiService.put<CalendarEventDto>(
      Endpoint.CALENDAR_EVENT_BY_ID(this.accountId, eventId),
      request.getJsonObj()
    )

    return this.handleResponse<CalendarEventDto>(data, response => {
      this.upsertCalendarEvent(response)
      return response
    })
  }

  @Action
  async [CalendarActions.DELETE_CALENDAR_EVENT](eventId: string) {
    const data = await apiService.delete<void>(Endpoint.CALENDAR_EVENT_BY_ID(this.accountId, eventId))

    return this.handleResponse<void>(data, () => {
      this.removeCalendarEvent(eventId)
      return true
    })
  }

  @Action
  async [CalendarActions.RESET_CALENDAR_EVENTS]() {
    this.resetCalendarEvents()
  }
}

export const calendarModule = new CalendarModule({
  store,
  name: "CALENDAR"
})
